import React, { Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import "./styles/immersive.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import PromoBanner from "./components/PromoBanner";
import ExitIntentPopup from "./components/ExitIntentPopup";
import SocialProofNotification from "./components/SocialProofNotification";
import SeoSchema from "./components/SeoSchema";
import ChatbotWidget from "./components/ChatbotWidget";

// Eagerly load original homepage (critical path)
import HomePage from "./pages/HomePage";
const HomePageGlobal = React.lazy(() => import("./pages/HomePageGlobal"));
const MunicipalNetworkPage = React.lazy(() => import("./pages/MunicipalNetworkPage"));

// Network platform pages
const NetworkMapPage = React.lazy(() => import("./pages/NetworkMapPage"));
const MunicipalityPage = React.lazy(() => import("./pages/MunicipalityPage"));
const HeroPreviewPage = React.lazy(() => import("./pages/HeroPreviewPage"));

// Lazy load all other pages
const ServicesPage = React.lazy(() => import("./pages/ServicesPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const StoriesPage = React.lazy(() => import("./pages/StoriesPage"));
const BlogPage = React.lazy(() => import("./pages/BlogPage"));
const BlogPostPage = React.lazy(() => import("./pages/BlogPostPage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const PricingPage = React.lazy(() => import("./pages/PricingPage"));
const TeamPage = React.lazy(() => import("./pages/TeamPage"));
const QuickHelpPage = React.lazy(() => import("./pages/QuickHelpPage"));
const ScanRecordsPage = React.lazy(() => import("./pages/ScanRecordsPage"));
const AncestralPlacePage = React.lazy(() => import("./pages/AncestralPlacePage"));
const HeritageTravelPage = React.lazy(() => import("./pages/HeritageTravelPage"));
const ItalianForGenealogyPage = React.lazy(() => import("./pages/ItalianForGenealogyPage"));
const CemeteryPhotoServicePage = React.lazy(() => import("./pages/CemeteryPhotoServicePage"));
const HeritageRecipesPage = React.lazy(() => import("./pages/HeritageRecipesPage"));
const SurnameOriginsPage = React.lazy(() => import("./pages/SurnameOriginsPage"));
const EmigrationPathsPage = React.lazy(() => import("./pages/EmigrationPathsPage"));
const RecipePremiumPage = React.lazy(() => import("./pages/RecipePremiumPage"));
const ResearchPublicationsPage = React.lazy(() => import("./pages/ResearchPublicationsPage"));
const ResearchHubPage = React.lazy(() => import("./pages/ResearchHubPage"));
const DonatePage = React.lazy(() => import("./pages/DonatePage"));
const AdminEmailsPage = React.lazy(() => import("./pages/AdminEmailsPage"));
const MunicipalitiesNetworkHubPage = React.lazy(() => import("./pages/MunicipalitiesNetworkHubPage"));
const InstitutionDetailPage = React.lazy(() => import("./pages/InstitutionDetailPage"));
const AcademyPage = React.lazy(() => import("./pages/AcademyPage"));
const AttestatiPage = React.lazy(() => import("./pages/academy/AttestatiPage"));
const MaterialeDidatticoPage = React.lazy(() => import("./pages/academy/MaterialeDidatticoPage"));
const DiventaTutorPage = React.lazy(() => import("./pages/academy/DiventaTutorPage"));
const AcademyFAQPage = React.lazy(() => import("./pages/academy/FAQPage"));
const DatiLegaliPage = React.lazy(() => import("./pages/DatiLegaliPage"));
const AdminGrantsPage = React.lazy(() => import("./pages/AdminGrantsPage"));
const GrantConsultancyPage = React.lazy(() => import("./pages/GrantConsultancyPage"));
const BandiHubComingSoonPage = React.lazy(() => import("./pages/BandiHubComingSoonPage"));
const GuideBandiPage = React.lazy(() => import("./pages/GuideBandiPage"));
const ResourcesPage = React.lazy(() => import("./pages/ResourcesPage"));
const FreeEvaluationPage = React.lazy(() => import("./pages/FreeEvaluationPage"));
const TermsPage = React.lazy(() => import("./pages/TermsPage"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const CookiePage = React.lazy(() => import("./pages/CookiePage"));
const ThankYouPage = React.lazy(() => import("./pages/ThankYouPage"));
const PrivilegesAreaPage = React.lazy(() => import("./pages/PrivilegesAreaPage"));
const PrivilegesAdminPage = React.lazy(() => import("./pages/PrivilegesAdminPage"));
const AdminStatisticsPage = React.lazy(() => import("./pages/AdminStatisticsPage"));
const AdminNetworkTrendsPage = React.lazy(() => import("./pages/AdminNetworkTrendsPage"));
const AdminNetworkLeadsPage = React.lazy(() => import("./pages/AdminNetworkLeadsPage"));
const AdminIntelligenceCenterPage = React.lazy(() => import("./pages/AdminIntelligenceCenterPage"));
const BlogCmsPage = React.lazy(() => import("./pages/BlogCmsPage"));
const BecomePartnerPage = React.lazy(() => import("./pages/BecomePartnerPage"));
const PartnerDashboardPage = React.lazy(() => import("./pages/PartnerDashboardPage"));
const PrivatePartnerOfferPage = React.lazy(() => import("./pages/PrivatePartnerOfferPage"));
const PublicPartnerPricingPage = React.lazy(() => import("./pages/PublicPartnerPricingPage"));
const HoneymoonPage = React.lazy(() => import("./pages/HoneymoonPage"));
const SpringPromoPage = React.lazy(() => import("./pages/SpringPromoPage"));
const FAQPage = React.lazy(() => import("./pages/FAQPage"));
const GalleryPage = React.lazy(() => import("./pages/GalleryPage"));
const ItalyMapPage = React.lazy(() => import("./pages/ItalyMapPage"));
const CertificatePage = React.lazy(() => import("./pages/CertificatePage"));
const BookingPage = React.lazy(() => import("./pages/BookingPage"));
const ReviewsPage = React.lazy(() => import("./pages/ReviewsPage"));

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
      <div className="App min-h-screen flex flex-col">
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </div>
    </LanguageProvider>
    </HelmetProvider>
  );
}

const isAdminRoute = (pathname) => {
  const p = (pathname || '').toLowerCase();
  return p.startsWith('/ceo') || p.startsWith('/admin');
};

function AppShell() {
  const location = useLocation();
  const adminMode = isAdminRoute(location.pathname);
  return (
    <>
      {!adminMode && <PromoBanner />}
      {!adminMode && <Navbar />}
      <main className="flex-grow">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/network/hub" element={<Navigate to="/rete-comuni" replace />} />
                <Route path="/network/map" element={<NetworkMapPage />} />
                <Route path="/network/join" element={<Navigate to="/become-partner" replace />} />
                <Route path="/network/comuni/:slug" element={<MunicipalityPage />} />
                <Route path="/network/municipality/:slug" element={<MunicipalityPage />} />
                <Route path="/preview/hero" element={<HeroPreviewPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/stories" element={<StoriesPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:postId" element={<BlogPostPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/quick-help" element={<QuickHelpPage />} />
                <Route path="/scan-records" element={<ScanRecordsPage />} />
                <Route path="/ancestral-places" element={<AncestralPlacePage />} />
                <Route path="/heritage-travel" element={<HeritageTravelPage />} />
                <Route path="/italian-for-genealogy" element={<ItalianForGenealogyPage />} />
                <Route path="/cemetery-photo-service" element={<CemeteryPhotoServicePage />} />
                <Route path="/heritage-recipes" element={<HeritageRecipesPage />} />
                <Route path="/surname-origins" element={<SurnameOriginsPage />} />
                <Route path="/emigration-paths" element={<EmigrationPathsPage />} />
                <Route path="/recipe-premium" element={<RecipePremiumPage />} />
                <Route path="/research-publications" element={<ResearchPublicationsPage />} />
                <Route path="/research-hub" element={<ResearchHubPage />} />
                <Route path="/donate" element={<DonatePage />} />
                <Route path="/admin/emails" element={<AdminEmailsPage />} />
                <Route path="/CEO" element={<AdminEmailsPage />} />
                <Route path="/ceo" element={<AdminEmailsPage />} />
                <Route path="/rete-comuni" element={<HomePageGlobal />} />
                <Route path="/rete-internazionale" element={<MunicipalNetworkPage />} />
                <Route path="/international-network" element={<Navigate to="/rete-internazionale" replace />} />
                <Route path="/academy" element={<AcademyPage />} />
                <Route path="/academy/attestati" element={<AttestatiPage />} />
                <Route path="/academy/materiale-didattico" element={<MaterialeDidatticoPage />} />
                <Route path="/academy/diventa-tutor" element={<DiventaTutorPage />} />
                <Route path="/academy/faq" element={<AcademyFAQPage />} />
                <Route path="/dati-legali" element={<DatiLegaliPage />} />
                <Route path="/legal" element={<DatiLegaliPage />} />
                <Route path="/istituzioni" element={<MunicipalitiesNetworkHubPage />} />
                <Route path="/istituzioni/:slug" element={<InstitutionDetailPage />} />
                <Route path="/rete-comuni/:slug" element={<Navigate to="/istituzioni" replace />} />
                {/* Legacy redirects from the previous "Istituzioni" branding */}
                <Route path="/institutions" element={<Navigate to="/istituzioni" replace />} />
                <Route path="/institutions/:slug" element={<InstitutionDetailPage />} />
                <Route path="/free-evaluation" element={<FreeEvaluationPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/cookies" element={<CookiePage />} />
                <Route path="/thank-you" element={<ThankYouPage />} />
                <Route path="/privileges" element={<PrivilegesAreaPage />} />
                <Route path="/privileges/:partnerId" element={<PrivilegesAreaPage />} />
                {/* Legacy redirects from the old auth-gated structure */}
                <Route path="/privileges/area" element={<Navigate to="/privileges" replace />} />
                <Route path="/privileges/area/:partnerId" element={<Navigate to="/privileges" replace />} />
                <Route path="/privileges/admin" element={<PrivilegesAdminPage />} />
                <Route path="/admin/statistics" element={<AdminStatisticsPage />} />
                <Route path="/admin/network-trends" element={<AdminNetworkTrendsPage />} />
                <Route path="/admin/network-leads" element={<AdminNetworkLeadsPage />} />
                <Route path="/admin/intelligence" element={<AdminIntelligenceCenterPage />} />
                <Route path="/admin/grants" element={<AdminGrantsPage />} />
                <Route path="/grant-consultancy" element={<GrantConsultancyPage />} />
                <Route path="/bandi-finanziamenti" element={<GrantConsultancyPage />} />
                <Route path="/finanziamenti-comuni" element={<GrantConsultancyPage />} />
                <Route path="/bandi/osservatorio" element={<BandiHubComingSoonPage section="osservatorio" />} />
                <Route path="/bandi/casi-studio" element={<BandiHubComingSoonPage section="casi-studio" />} />
                <Route path="/guida-bandi-comuni-2026" element={<GuideBandiPage />} />
                <Route path="/bandi/guida-2026" element={<GuideBandiPage />} />
                <Route path="/risorse" element={<ResourcesPage />} />
                <Route path="/resources" element={<ResourcesPage />} />
                <Route path="/admin/cms" element={<BlogCmsPage />} />
                <Route path="/become-partner" element={<BecomePartnerPage />} />
                <Route path="/partner/dashboard" element={<PartnerDashboardPage />} />
                <Route path="/partner/offer" element={<PrivatePartnerOfferPage />} />
                <Route path="/partner/pricing" element={<PublicPartnerPricingPage />} />
                <Route path="/honeymoon" element={<HoneymoonPage />} />
                <Route path="/spring-offer" element={<SpringPromoPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/italy-map" element={<ItalyMapPage />} />
                <Route path="/certificate" element={<CertificatePage />} />
                <Route path="/booking" element={<BookingPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
              </Routes>
            </Suspense>
          </main>
          {!adminMode && <Footer />}
          {!adminMode && <ExitIntentPopup />}
          {!adminMode && <SocialProofNotification />}
          {!adminMode && <ChatbotWidget />}
          <SeoSchema />
          <CookieConsent />
    </>
  );
}

export default App;
